import { and, eq } from 'drizzle-orm'
import { db } from '@/db'
import {
  auditLog,
  kbDraftRetrievals,
  knowledgeEntries,
  ticketMessages,
  tickets,
} from '@/db/schema'
import { normalizeUserTimeZone } from '@/lib/user-time-zone'

export type EngagementRole = 'line_manager' | 'expert' | 'admin'

export type LineManagerEngagementStats = {
  ticketsThisMonth: number
  ticketsLastMonth: number
  weekStreak: number
  resolved: number
}

export type ExpertEngagementStats = {
  activeKbArticles: number
  responsesHelped: number
}

export type ActivityCalendarDay = {
  date: string
  count: number
  level: 0 | 1 | 2 | 3
  isFuture: boolean
}

export type ActivityCalendarWeek = {
  weekStart: string
  days: ActivityCalendarDay[]
}

export type ActivityCalendar = {
  weeks: ActivityCalendarWeek[]
  streak: number
}

export type ActivityAction = {
  occurredAt: Date
  count?: number
}

export type ProfileActivity = {
  calendar: ActivityCalendar
  totalActions: number
}

function localDateParts(date: Date, timeZone: string): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)

  const year = Number(parts.find((part) => part.type === 'year')?.value)
  const month = Number(parts.find((part) => part.type === 'month')?.value)
  const day = Number(parts.find((part) => part.type === 'day')?.value)
  return { year, month, day }
}

function dateKeyFromParts(parts: { year: number; month: number; day: number }): string {
  return `${parts.year.toString().padStart(4, '0')}-${parts.month.toString().padStart(2, '0')}-${parts.day.toString().padStart(2, '0')}`
}

function localDateKey(date: Date, timeZone: string): string {
  return dateKeyFromParts(localDateParts(date, timeZone))
}

function utcDateFromKey(dateKey: string): Date {
  const [year, month, day] = dateKey.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day))
}

function dateKeyFromUtcDate(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function addDaysToKey(dateKey: string, days: number): string {
  const date = utcDateFromKey(dateKey)
  date.setUTCDate(date.getUTCDate() + days)
  return dateKeyFromUtcDate(date)
}

function addMonthsToParts(parts: { year: number; month: number; day: number }, months: number): { year: number; month: number; day: number } {
  const date = new Date(Date.UTC(parts.year, parts.month - 1 + months, 1))
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: 1 }
}

function monthKey(parts: { year: number; month: number }): string {
  return `${parts.year.toString().padStart(4, '0')}-${parts.month.toString().padStart(2, '0')}`
}

function isoWeekStartKey(dateKey: string): string {
  const date = utcDateFromKey(dateKey)
  const day = date.getUTCDay()
  const mondayOffset = day === 0 ? -6 : 1 - day
  date.setUTCDate(date.getUTCDate() + mondayOffset)
  return dateKeyFromUtcDate(date)
}

function activityLevel(count: number): 0 | 1 | 2 | 3 {
  if (count <= 0) return 0
  if (count === 1) return 1
  if (count <= 4) return 2
  return 3
}

function countDistinct(values: Array<string | null>): number {
  return new Set(values.filter((value): value is string => Boolean(value))).size
}

export function calculateWeekStreak(submissionDates: Date[], timeZone: string | null | undefined, now = new Date()): number {
  const normalizedTimeZone = normalizeUserTimeZone(timeZone)
  const weeks = new Set(
    submissionDates.map((date) => isoWeekStartKey(localDateKey(date, normalizedTimeZone))),
  )
  if (weeks.size === 0) return 0

  const currentWeek = isoWeekStartKey(localDateKey(now, normalizedTimeZone))
  let cursor = weeks.has(currentWeek) ? currentWeek : addDaysToKey(currentWeek, -7)
  let streak = 0

  while (weeks.has(cursor)) {
    streak += 1
    cursor = addDaysToKey(cursor, -7)
  }

  return streak
}

export function buildActivityCalendar({
  actions,
  timeZone,
  now = new Date(),
}: {
  actions: ActivityAction[]
  timeZone: string | null | undefined
  now?: Date
}): ActivityCalendar {
  const normalizedTimeZone = normalizeUserTimeZone(timeZone)
  const todayKey = localDateKey(now, normalizedTimeZone)
  const currentWeekStart = isoWeekStartKey(todayKey)
  const firstWeekStart = addDaysToKey(currentWeekStart, -51 * 7)
  const countsByDay = new Map<string, number>()

  for (const action of actions) {
    const key = localDateKey(action.occurredAt, normalizedTimeZone)
    countsByDay.set(key, (countsByDay.get(key) ?? 0) + (action.count ?? 1))
  }

  const weeks: ActivityCalendarWeek[] = []
  for (let weekIndex = 0; weekIndex < 52; weekIndex += 1) {
    const weekStart = addDaysToKey(firstWeekStart, weekIndex * 7)
    const days: ActivityCalendarDay[] = []
    for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
      const date = addDaysToKey(weekStart, dayIndex)
      const count = countsByDay.get(date) ?? 0
      days.push({ date, count, level: activityLevel(count), isFuture: date > todayKey })
    }
    weeks.push({ weekStart, days })
  }

  return {
    weeks,
    streak: calculateWeekStreak(actions.map((action) => action.occurredAt), normalizedTimeZone, now),
  }
}

export async function getLineManagerEngagementStats({
  tenantId,
  userId,
  timeZone,
  now = new Date(),
}: {
  tenantId: string
  userId: string
  timeZone: string | null | undefined
  now?: Date
}): Promise<LineManagerEngagementStats> {
  const normalizedTimeZone = normalizeUserTimeZone(timeZone)
  const nowParts = localDateParts(now, normalizedTimeZone)
  const currentMonth = monthKey(nowParts)
  const previousMonth = monthKey(addMonthsToParts(nowParts, -1))

  const userTickets = await db
    .select({
      id: tickets.id,
      status: tickets.status,
      createdAt: tickets.createdAt,
    })
    .from(tickets)
    .where(and(eq(tickets.tenantId, tenantId), eq(tickets.submitterId, userId)))

  return {
    ticketsThisMonth: userTickets.filter((ticket) => monthKey(localDateParts(ticket.createdAt, normalizedTimeZone)) === currentMonth).length,
    ticketsLastMonth: userTickets.filter((ticket) => monthKey(localDateParts(ticket.createdAt, normalizedTimeZone)) === previousMonth).length,
    weekStreak: calculateWeekStreak(userTickets.map((ticket) => ticket.createdAt), normalizedTimeZone, now),
    resolved: userTickets.filter((ticket) => ticket.status === 'CLOSED').length,
  }
}

export async function getKbEntryImpactCount({
  tenantId,
  entryId,
}: {
  tenantId: string
  entryId: string
}): Promise<number> {
  const rows = await db
    .select({ ticketId: kbDraftRetrievals.ticketId })
    .from(kbDraftRetrievals)
    .innerJoin(tickets, eq(tickets.id, kbDraftRetrievals.ticketId))
    .where(and(
      eq(kbDraftRetrievals.tenantId, tenantId),
      eq(kbDraftRetrievals.kbEntryId, entryId),
      eq(tickets.status, 'RESPONDED'),
    ))

  return countDistinct(rows.map((row) => row.ticketId))
}

export async function getExpertEngagementStats({
  tenantId,
  userId,
}: {
  tenantId: string
  userId: string
}): Promise<ExpertEngagementStats> {
  const [entries, responseRows] = await Promise.all([
    db
      .select({ id: knowledgeEntries.id })
      .from(knowledgeEntries)
      .where(and(
        eq(knowledgeEntries.tenantId, tenantId),
        eq(knowledgeEntries.createdById, userId),
        eq(knowledgeEntries.isDeleted, false),
      )),
    db
      .select({ ticketId: kbDraftRetrievals.ticketId })
      .from(kbDraftRetrievals)
      .innerJoin(knowledgeEntries, eq(knowledgeEntries.id, kbDraftRetrievals.kbEntryId))
      .innerJoin(tickets, eq(tickets.id, kbDraftRetrievals.ticketId))
      .where(and(
        eq(kbDraftRetrievals.tenantId, tenantId),
        eq(knowledgeEntries.createdById, userId),
        eq(tickets.status, 'RESPONDED'),
      )),
  ])

  return {
    activeKbArticles: entries.length,
    responsesHelped: countDistinct(responseRows.map((row) => row.ticketId)),
  }
}

export async function getProfileActivity({
  tenantId,
  userId,
  role,
  timeZone,
  now = new Date(),
}: {
  tenantId: string
  userId: string
  role: EngagementRole
  timeZone: string | null | undefined
  now?: Date
}): Promise<ProfileActivity> {
  let actions: ActivityAction[]

  if (role === 'line_manager') {
    const rows = await db
      .select({ occurredAt: tickets.createdAt })
      .from(tickets)
      .where(and(eq(tickets.tenantId, tenantId), eq(tickets.submitterId, userId)))
    actions = rows
  } else {
    const [messages, entries, approvals] = await Promise.all([
      db
        .select({ occurredAt: ticketMessages.createdAt })
        .from(ticketMessages)
        .where(and(
          eq(ticketMessages.tenantId, tenantId),
          eq(ticketMessages.authorId, userId),
          eq(ticketMessages.role, 'expert'),
        )),
      db
        .select({ occurredAt: knowledgeEntries.createdAt })
        .from(knowledgeEntries)
        .where(and(eq(knowledgeEntries.tenantId, tenantId), eq(knowledgeEntries.createdById, userId))),
      db
        .select({ occurredAt: auditLog.createdAt })
        .from(auditLog)
        .where(and(
          eq(auditLog.tenantId, tenantId),
          eq(auditLog.actorId, userId),
          eq(auditLog.action, 'knowledge.proposal_accepted'),
        )),
    ])
    actions = [...messages, ...entries, ...approvals]
  }

  return {
    calendar: buildActivityCalendar({ actions, timeZone, now }),
    totalActions: actions.length,
  }
}
